import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { fetchPublicacionAuditoriaPrecioStockApi } from '../../../../api/axios';
import './AuditoriaPrecioStock.css';

const buildEmptyResponse = () => ({
    resumen: {
        precioListaActual: null,
        precioPromocionalActual: null,
        stockActual: null,
        ventas90Dias: 0,
        cantidadAlertas: 0
    },
    periodos: []
});

const toArray = (value, alternate) => {
    if (Array.isArray(value)) return value;
    if (Array.isArray(alternate)) return alternate;
    return [];
};

const toNumberOrNull = (value) => {
    if (value == null || value === '') return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
};

const toMoneyOrNull = (value) => {
    if (value == null || value === '') return null;
    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : null;
    }
    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) return null;

        const sanitized = trimmed
            .replace(/[^\d,.-]/g, '')
            .replace(/\s+/g, '');

        if (!sanitized) return null;

        const sign = sanitized.startsWith('-') ? '-' : '';
        const unsigned = sanitized.replace(/-/g, '');

        if (!unsigned) return null;

        const strategies = [
            [/^\d{1,3}(\.\d{3})+,\d{1,4}$/, (raw) => raw.replace(/\./g, '').replace(',', '.')],
            [/^\d{1,3}(,\d{3})+\.\d{1,4}$/, (raw) => raw.replace(/,/g, '')],
            [/^\d{1,3}(\.\d{3})+$/, (raw) => raw.replace(/\./g, '')],
            [/^\d{1,3}(,\d{3})+$/, (raw) => raw.replace(/,/g, '')],
            [/^\d+,\d{1,4}$/, (raw) => raw.replace(',', '.')],
            [/^\d+\.\d{1,4}$/, (raw) => raw],
            [/^\d+$/, (raw) => raw]
        ];

        let normalized = null;

        for (const [pattern, transform] of strategies) {
            if (pattern.test(unsigned)) {
                normalized = transform(unsigned);
                break;
            }
        }

        if (normalized == null) {
            const lastComma = unsigned.lastIndexOf(',');
            const lastDot = unsigned.lastIndexOf('.');
            const lastSeparatorIndex = Math.max(lastComma, lastDot);

            if (lastSeparatorIndex >= 0) {
                const integerPart = unsigned.slice(0, lastSeparatorIndex).replace(/[.,]/g, '');
                const decimalPart = unsigned.slice(lastSeparatorIndex + 1).replace(/[.,]/g, '');
                normalized = decimalPart ? `${integerPart}.${decimalPart}` : integerPart;
            } else {
                normalized = unsigned.replace(/[.,]/g, '');
            }
        }

        const parsed = Number(`${sign}${normalized}`);
        return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
};

const computePercentDelta = (before, after) => {
    if (!Number.isFinite(before) || !Number.isFinite(after) || before === 0) return null;
    return ((after - before) / Math.abs(before)) * 100;
};

const maybeFixShiftedPromotionalValue = (value, listPrice, referenceValue) => {
    if (
        !Number.isFinite(value) ||
        value <= 0 ||
        value < 100000 ||
        !Number.isInteger(value) ||
        value % 100 !== 0
    ) {
        return value;
    }

    const candidate = value / 100;
    const hasListPrice = Number.isFinite(listPrice) && listPrice > 0;
    const hasReferenceValue = Number.isFinite(referenceValue) && referenceValue > 0;

    const rawRatioToList = hasListPrice ? (value / listPrice) : null;
    const candidateRatioToList = hasListPrice ? (candidate / listPrice) : null;
    const rawDistanceToReference = hasReferenceValue ? Math.abs(value - referenceValue) : null;
    const candidateDistanceToReference = hasReferenceValue ? Math.abs(candidate - referenceValue) : null;

    const looksShiftedAgainstList = hasListPrice
        && rawRatioToList > 20
        && candidateRatioToList <= 5;

    const candidateIsMuchCloserToReference = hasReferenceValue
        && candidateDistanceToReference * 10 <= rawDistanceToReference;

    return (looksShiftedAgainstList || candidateIsMuchCloserToReference) ? candidate : value;
};

const normalizeAlert = (alerta) => {
    if (!alerta) return null;
    if (typeof alerta === 'string') {
        return {
            codigo: alerta,
            descripcion: alerta.replace(/_/g, ' ')
        };
    }

    return {
        codigo: alerta?.codigo ?? alerta?.Codigo ?? alerta?.id ?? alerta?.Id ?? 'alerta',
        descripcion: alerta?.descripcion ?? alerta?.Descripcion ?? alerta?.nombre ?? alerta?.Nombre ?? 'Alerta'
    };
};

const normalizeEvento = (evento, index, periodContext = {}) => {
    const antes = evento?.antes ?? evento?.Antes ?? {};
    const despues = evento?.despues ?? evento?.Despues ?? {};
    const delta = evento?.delta ?? evento?.Delta ?? {};

    const antesPrecioLista = toMoneyOrNull(antes?.precioLista ?? antes?.PrecioLista);
    const despuesPrecioLista = toMoneyOrNull(despues?.precioLista ?? despues?.PrecioLista);
    const despuesPrecioPromocionalInicial = toMoneyOrNull(despues?.precioPromocional ?? despues?.PrecioPromocional);
    const antesPrecioPromocionalInicial = toMoneyOrNull(antes?.precioPromocional ?? antes?.PrecioPromocional);
    const despuesPrecioPromocional = maybeFixShiftedPromotionalValue(
        despuesPrecioPromocionalInicial,
        despuesPrecioLista,
        periodContext?.precioPromocional ?? antesPrecioPromocionalInicial
    );
    const antesPrecioPromocional = maybeFixShiftedPromotionalValue(
        antesPrecioPromocionalInicial,
        antesPrecioLista,
        despuesPrecioPromocional ?? periodContext?.precioPromocional
    );
    const antesStock = toNumberOrNull(antes?.stock ?? antes?.Stock);
    const despuesStock = toNumberOrNull(despues?.stock ?? despues?.Stock);

    return {
        id: evento?.id ?? evento?.Id ?? `${evento?.fecha ?? evento?.Fecha ?? 'evento'}-${index}`,
        fecha: evento?.fecha ?? evento?.Fecha ?? null,
        origen: evento?.origen ?? evento?.Origen ?? '-',
        usuario: evento?.usuario ?? evento?.Usuario ?? '-',
        idUsuario: evento?.idUsuario ?? evento?.IdUsuario ?? null,
        antes: {
            precioLista: antesPrecioLista,
            precioPromocional: antesPrecioPromocional,
            stock: antesStock
        },
        despues: {
            precioLista: despuesPrecioLista,
            precioPromocional: despuesPrecioPromocional,
            stock: despuesStock
        },
        delta: {
            precioListaPct: computePercentDelta(antesPrecioLista, despuesPrecioLista)
                ?? toNumberOrNull(delta?.precioListaPct ?? delta?.PrecioListaPct),
            precioPromocionalPct: computePercentDelta(antesPrecioPromocional, despuesPrecioPromocional)
                ?? toNumberOrNull(delta?.precioPromocionalPct ?? delta?.PrecioPromocionalPct),
            stockPct: computePercentDelta(antesStock, despuesStock)
                ?? toNumberOrNull(delta?.stockPct ?? delta?.StockPct)
        },
        alertas: toArray(evento?.alertas, evento?.Alertas).map(normalizeAlert).filter(Boolean)
    };
};

const normalizePeriodo = (periodo, index) => {
    const precioLista = toMoneyOrNull(periodo?.precioLista ?? periodo?.PrecioLista);
    const precioPromocionalInicial = toMoneyOrNull(periodo?.precioPromocional ?? periodo?.PrecioPromocional);
    const precioPromocional = maybeFixShiftedPromotionalValue(
        precioPromocionalInicial,
        precioLista,
        null
    );

    return {
        id: periodo?.id ?? periodo?.Id ?? `${periodo?.fechaDesde ?? periodo?.FechaDesde ?? 'periodo'}-${index}`,
        fechaDesde: periodo?.fechaDesde ?? periodo?.FechaDesde ?? null,
        fechaHasta: periodo?.fechaHasta ?? periodo?.FechaHasta ?? null,
        precioLista,
        precioPromocional,
        stockInicial: toNumberOrNull(periodo?.stockInicial ?? periodo?.StockInicial),
        stockFinal: toNumberOrNull(periodo?.stockFinal ?? periodo?.StockFinal),
        cantidadVentas: toNumberOrNull(periodo?.cantidadVentas ?? periodo?.CantidadVentas) ?? 0,
        idsOrdenes: toArray(periodo?.idsOrdenes, periodo?.IdsOrdenes),
        alertas: toArray(periodo?.alertas, periodo?.Alertas).map(normalizeAlert).filter(Boolean),
        eventos: toArray(periodo?.eventos, periodo?.Eventos).map((evento, eventIndex) => normalizeEvento(evento, eventIndex, {
            precioLista,
            precioPromocional
        }))
    };
};

const normalizeAuditoriaResponse = (rawData) => {
    const data = rawData?.data ?? rawData ?? {};
    const resumen = data?.resumen ?? data?.Resumen ?? {};
    const periodos = toArray(data?.periodos, data?.Periodos).map(normalizePeriodo);

    return {
        resumen: {
            precioListaActual: toMoneyOrNull(resumen?.precioListaActual ?? resumen?.PrecioListaActual),
            precioPromocionalActual: toMoneyOrNull(resumen?.precioPromocionalActual ?? resumen?.PrecioPromocionalActual),
            stockActual: toNumberOrNull(resumen?.stockActual ?? resumen?.StockActual),
            ventas90Dias: toNumberOrNull(resumen?.ventas90Dias ?? resumen?.Ventas90Dias) ?? 0,
            cantidadAlertas: toNumberOrNull(resumen?.cantidadAlertas ?? resumen?.CantidadAlertas) ?? 0
        },
        periodos
    };
};

const currencyFormatter = new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    maximumFractionDigits: 2
});

const integerFormatter = new Intl.NumberFormat('es-AR');

const formatMoney = (value) => {
    if (value == null) return '-';
    return currencyFormatter.format(value);
};

const formatInteger = (value) => {
    if (value == null) return '-';
    return integerFormatter.format(value);
};

const formatPercent = (value) => {
    if (value == null) return '-';
    const sign = value > 0 ? '+' : '';
    return `${sign}${value.toFixed(2)}%`;
};

const formatDate = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString('es-AR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

const monthsOptions = [
    { label: '30 dias', value: 1 },
    { label: '60 dias', value: 2 },
    { label: '90 dias', value: 3 }
];

function AuditoriaPrecioStock({ idPublicacion, publicacion }) {
    const [meses, setMeses] = useState(3);
    const [soloAlertas, setSoloAlertas] = useState(false);
    const [soloCambiosReales, setSoloCambiosReales] = useState(true);
    const [expandedPeriodId, setExpandedPeriodId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [response, setResponse] = useState(buildEmptyResponse());

    useEffect(() => {
        let active = true;

        const fetchData = async () => {
            if (!idPublicacion) {
                setResponse(buildEmptyResponse());
                return;
            }

            setLoading(true);
            setError('');

            try {
                const data = await fetchPublicacionAuditoriaPrecioStockApi({
                    idPublicacion,
                    meses,
                    soloAlertas,
                    soloCambiosReales
                });

                if (!active) return;
                setResponse(normalizeAuditoriaResponse(data));
            } catch (err) {
                if (!active) return;

                const status = err?.response?.status;
                if (status === 404) {
                    setError('La auditoria todavia no esta disponible para esta publicacion.');
                } else {
                    setError('No se pudo cargar la auditoria de precio y stock.');
                }
                setResponse(buildEmptyResponse());
            } finally {
                if (active) {
                    setLoading(false);
                }
            }
        };

        fetchData();

        return () => {
            active = false;
        };
    }, [idPublicacion, meses, soloAlertas, soloCambiosReales]);

    const resumen = useMemo(() => ({
        precioListaActual: response?.resumen?.precioListaActual ?? toMoneyOrNull(publicacion?.precioLista),
        precioPromocionalActual: response?.resumen?.precioPromocionalActual ?? toMoneyOrNull(publicacion?.precioPromocional),
        stockActual: response?.resumen?.stockActual ?? toNumberOrNull(publicacion?.stock),
        ventas90Dias: response?.resumen?.ventas90Dias ?? 0,
        cantidadAlertas: response?.resumen?.cantidadAlertas ?? 0
    }), [response, publicacion]);

    const periodos = response?.periodos ?? [];

    return (
        <div className="auditoria-publicacion">
            <div className="auditoria-publicacion__summary">
                <div className="auditoria-publicacion__card">
                    <span className="auditoria-publicacion__label">Precio lista actual</span>
                    <strong>{formatMoney(resumen.precioListaActual)}</strong>
                </div>
                <div className="auditoria-publicacion__card">
                    <span className="auditoria-publicacion__label">Precio promo actual</span>
                    <strong>{formatMoney(resumen.precioPromocionalActual)}</strong>
                </div>
                <div className="auditoria-publicacion__card">
                    <span className="auditoria-publicacion__label">Stock actual</span>
                    <strong>{formatInteger(resumen.stockActual)}</strong>
                </div>
                <div className="auditoria-publicacion__card">
                    <span className="auditoria-publicacion__label">Ventas del periodo</span>
                    <strong>{formatInteger(resumen.ventas90Dias)}</strong>
                </div>
                <div className="auditoria-publicacion__card auditoria-publicacion__card--alert">
                    <span className="auditoria-publicacion__label">Alertas activas</span>
                    <strong>{formatInteger(resumen.cantidadAlertas)}</strong>
                </div>
            </div>

            <div className="auditoria-publicacion__toolbar">
                <div className="auditoria-publicacion__months">
                    {monthsOptions.map((option) => (
                        <button
                            key={option.value}
                            type="button"
                            className={`auditoria-publicacion__filter-btn ${meses === option.value ? 'is-active' : ''}`}
                            onClick={() => setMeses(option.value)}
                        >
                            {option.label}
                        </button>
                    ))}
                </div>
                <label className="auditoria-publicacion__checkbox">
                    <input
                        type="checkbox"
                        checked={soloCambiosReales}
                        onChange={(event) => setSoloCambiosReales(event.target.checked)}
                    />
                    <span>Ocultar actualizaciones sin cambio real</span>
                </label>
                <label className="auditoria-publicacion__checkbox">
                    <input
                        type="checkbox"
                        checked={soloAlertas}
                        onChange={(event) => setSoloAlertas(event.target.checked)}
                    />
                    <span>Solo alertas</span>
                </label>
            </div>

            <div className="auditoria-publicacion__hint">
                Se muestran hasta 3 meses de cambios y se omiten eventos sin variacion real de precio o stock.
            </div>

            {loading && (
                <div className="auditoria-publicacion__state">
                    Cargando auditoria...
                </div>
            )}

            {!loading && error && (
                <div className="auditoria-publicacion__state auditoria-publicacion__state--error">
                    {error}
                </div>
            )}

            {!loading && !error && periodos.length === 0 && (
                <div className="auditoria-publicacion__state">
                    No hay cambios auditables para el periodo seleccionado.
                </div>
            )}

            {!loading && !error && periodos.length > 0 && (
                <div className="auditoria-publicacion__table-wrap">
                    <table className="table auditoria-publicacion__table">
                        <thead>
                            <tr>
                                <th>Periodo</th>
                                <th>Precio lista</th>
                                <th>Precio promo</th>
                                <th>Stock</th>
                                <th>Ventas</th>
                                <th>Ordenes</th>
                                <th>Alertas</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {periodos.map((periodo) => {
                                const expanded = expandedPeriodId === periodo.id;
                                return (
                                    <React.Fragment key={periodo.id}>
                                        <tr>
                                            <td>
                                                <div className="auditoria-publicacion__period">
                                                    <strong>{formatDate(periodo.fechaDesde)}</strong>
                                                    <span>hasta {formatDate(periodo.fechaHasta)}</span>
                                                </div>
                                            </td>
                                            <td>{formatMoney(periodo.precioLista)}</td>
                                            <td>{formatMoney(periodo.precioPromocional)}</td>
                                            <td>
                                                <div className="auditoria-publicacion__stock-cell">
                                                    <span>Inicio: {formatInteger(periodo.stockInicial)}</span>
                                                    <span>Cierre: {formatInteger(periodo.stockFinal)}</span>
                                                </div>
                                            </td>
                                            <td>{formatInteger(periodo.cantidadVentas)}</td>
                                            <td>{periodo.idsOrdenes?.length ? periodo.idsOrdenes.join(', ') : '-'}</td>
                                            <td>
                                                <div className="auditoria-publicacion__alerts">
                                                    {periodo.alertas?.length
                                                        ? periodo.alertas.map((alerta) => (
                                                            <span key={`${periodo.id}-${alerta.codigo}`} className="auditoria-publicacion__badge">
                                                                {alerta.descripcion}
                                                            </span>
                                                        ))
                                                        : <span className="auditoria-publicacion__muted">Sin alertas</span>}
                                                </div>
                                            </td>
                                            <td className="text-right">
                                                <button
                                                    type="button"
                                                    className="auditoria-publicacion__toggle"
                                                    onClick={() => setExpandedPeriodId(expanded ? null : periodo.id)}
                                                >
                                                    {expanded ? 'Ocultar detalle' : 'Ver detalle'}
                                                </button>
                                            </td>
                                        </tr>
                                        {expanded && (
                                            <tr className="auditoria-publicacion__detail-row">
                                                <td colSpan="8">
                                                    {periodo.eventos?.length ? (
                                                        <table className="table auditoria-publicacion__events-table">
                                                            <thead>
                                                                <tr>
                                                                    <th>Fecha</th>
                                                                    <th>Origen</th>
                                                                    <th>Usuario</th>
                                                                    <th>Antes</th>
                                                                    <th>Despues</th>
                                                                    <th>Delta</th>
                                                                    <th>Alertas</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {periodo.eventos.map((evento) => (
                                                                    <tr key={evento.id}>
                                                                        <td>{formatDate(evento.fecha)}</td>
                                                                        <td>{evento.origen}</td>
                                                                        <td>{evento.usuario}{evento.idUsuario ? ` (#${evento.idUsuario})` : ''}</td>
                                                                        <td>
                                                                            <div>Lista: {formatMoney(evento.antes.precioLista)}</div>
                                                                            <div>Promo: {formatMoney(evento.antes.precioPromocional)}</div>
                                                                            <div>Stock: {formatInteger(evento.antes.stock)}</div>
                                                                        </td>
                                                                        <td>
                                                                            <div>Lista: {formatMoney(evento.despues.precioLista)}</div>
                                                                            <div>Promo: {formatMoney(evento.despues.precioPromocional)}</div>
                                                                            <div>Stock: {formatInteger(evento.despues.stock)}</div>
                                                                        </td>
                                                                        <td>
                                                                            <div>Lista: {formatPercent(evento.delta.precioListaPct)}</div>
                                                                            <div>Promo: {formatPercent(evento.delta.precioPromocionalPct)}</div>
                                                                            <div>Stock: {formatPercent(evento.delta.stockPct)}</div>
                                                                        </td>
                                                                        <td>
                                                                            <div className="auditoria-publicacion__alerts">
                                                                                {evento.alertas?.length
                                                                                    ? evento.alertas.map((alerta) => (
                                                                                        <span key={`${evento.id}-${alerta.codigo}`} className="auditoria-publicacion__badge">
                                                                                            {alerta.descripcion}
                                                                                        </span>
                                                                                    ))
                                                                                    : <span className="auditoria-publicacion__muted">-</span>}
                                                                            </div>
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    ) : (
                                                        <div className="auditoria-publicacion__muted">
                                                            Este periodo no trajo eventos detallados.
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

const mapStateToProps = (state) => ({
    publicacion: state?.publicaciones?.publicacion ?? {}
});

export default connect(mapStateToProps)(AuditoriaPrecioStock);
